import { Box, Button, Grid, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CreditRequestBox from './components/CreditRequestBox';
import { SendCreditReqRequest, SendCreditReqRequest_LineItems, CreditReviewRequest, CreditReviewRequest_LineItems } from '../../app/services/api/generated';
import React, { useState } from 'react';
import { Reason } from './enums/reason';
import { CreditRequestType } from './enums/credit-request-types';
import { CreditRequestStatus } from './enums/creditRequestStatus';
import SendCreditRequestModal from './modals/SendCreditRequestModal';
import SendIcon from '@mui/icons-material/Send';
import { ArrowLeft } from '@mui/icons-material';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { neukFranchises } from '../../enums/FranchiseNames';
import useNswagClient from '../../hooks/api/useNswagClient';
import useLogError from '../../hooks/useLogError';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';

const CreditRequest = () => {
  const { t } = useTranslation('createCreditRequest');
  const { id } = useParams<{ id: string | undefined }>();
  const navigate = useNavigate();
  const { sendCreditReview } = useNswagClient();
  const { logError } = useLogError();
  const PurchaseOrdersId = id ? parseInt(id) : undefined;
  const [sendCreditRequest, setSendCreditRequest] = useState<SendCreditReqRequest | undefined | null>();
  const [displayButton, setDisplayButton] = useState<boolean>(false); 
  const [displayReviewButton, setDisplayReviewButton] = useState<boolean>(false);
  const [creditRequestType, setCreditRequestType] = useState<number | undefined>();
  const [openSendRequestDialog, setOpenSendRequestDialog] = useState(false);
  const { selectedStore, hasPermissionTo } = React.useContext(UserContext);
  const [showResultMessage, setShowResultMessage] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [creditRequestStatus, setCreditRequestStatus] = useState<number>(0);

  const isNeukFranchises = neukFranchises.includes(selectedStore?.franchiseName ?? '');


  const handleSubmitCreditReview = () => {
    if (!sendCreditRequest || !sendCreditRequest.items) return;
    
    const lineItems: CreditReviewRequest_LineItems[] = sendCreditRequest.items.map(item => {
      return {
        itemNumber: item.itemNumber,
        isCreditApproved: item.isCreditApproved ?? false,
        
      };
    });
    
    const reviewRequest: CreditReviewRequest = {
      purchaseOrderHeaderId: sendCreditRequest.purchaseOrderHeaderId ?? 0,
      items: lineItems,
    };
    
    
    sendCreditReview(reviewRequest)
      .then((response) => {
        if (response.success) {
          navigate('/store/po-receipt');
        } else {
          setShowResultMessage({
            open: true,
            message: t('creditRequestReview.errorMessage'),
            severity: 'error',
          });
        }
      })
      .catch((error) => {
        setShowResultMessage({
          open: true,
          message: t('creditRequestReview.errorMessage'),
          severity: 'error',
        });
        logError(error);
      });
  };

  const handleRequestTypeChange = (newValue: number) => {
    setCreditRequestType(newValue);
  };
  const handleCloseRequestDialog = () => {
    setOpenSendRequestDialog(false);
  };
  const handleSubmit = () => {
    setOpenSendRequestDialog(true);
  };
  const isPhotoRequired = (item: SendCreditReqRequest_LineItems) => {
    const reason = sendCreditRequest?.items.find(x => x.itemNumber == item.itemNumber)?.reasonForCreditRequest;
    return sendCreditRequest?.customerRmaNumber ? false : reason === Reason.DAMAGE || reason === Reason.EXPIRED_STALE_PRODUCT;
  };
  const isValid = () => {
    return (
      sendCreditRequest?.items.every(item =>
        item.itemNumber &&
        item.reasonForCreditRequest &&
        (!isPhotoRequired(item) || (item.photos && item.photos.length > 0)))

    );
  }; 
  const disableSendButton = () => {
    return (
      !isValid() || (sendCreditRequest != null && creditRequestType === CreditRequestType.ReturnToVendor &&
        (!sendCreditRequest.deliveryNoteNumber ||
          sendCreditRequest.deliveryNoteNumber === sendCreditRequest.customerRmaNumber ||
          !sendCreditRequest.documents))
    );
  };
  const disableReviewButton = () => {
   
    if (!hasPermissionTo(['CreditRequestAdmin'])) {
      return true;
    }

    const allItemsHaveApprovalStatus = sendCreditRequest?.items.every(
      item => item.isCreditApproved === true || item.isCreditApproved === false,
    );
    return !(displayReviewButton && allItemsHaveApprovalStatus && creditRequestStatus === CreditRequestStatus.SENT);
  };
  const closeProcessingAlert = () => {
    setShowResultMessage({ ...showResultMessage, open: false });
  };
  return (
    <Box display="flex"
      flexDirection="column" >
      <Grid container
        sx={{ p: '30px' }}>
        <Grid item
          xs={12}
          sx={{ textAlign: 'left' }}>
          <Link
            onClick={() => navigate('/store/po-receipt')}
            underline="none"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <ArrowLeft /> {t('goBack')}
          </Link>
          <PageTitle>{t('creditRequest')} {PurchaseOrdersId}</PageTitle> 
        </Grid>
        <Grid item
          xs={12}
          sx={{ textAlign: 'right' }}>
            
          {displayButton && (sendCreditRequest?.items?.length ?? 0) > 0 && <Button variant="primary"
            onClick={handleSubmit}
            disabled={disableSendButton()}
            size="lg">
            <SendIcon /> {t('send')}
          </Button>}
          {displayReviewButton && (sendCreditRequest?.items?.length ?? 0) > 0 && <Button variant="primary"
            onClick={handleSubmitCreditReview}
            disabled={disableReviewButton()}
            size="lg">
            <SendIcon /> {t('creditRequestReview.submit')}
          </Button>}
        </Grid>
        <Grid item
          xs={12}
          sx={{ pt: '20px' }}>
          {<CreditRequestBox setSendCreditRequest={setSendCreditRequest}
            setDisplayButton={setDisplayButton} 
            onRequestTypeChange={handleRequestTypeChange} 
            purchaseOrderId={PurchaseOrdersId!}
            onInput={disableSendButton}
            setDisplaySubmitReviewButton={setDisplayReviewButton}
            setCreditRequestStatus={setCreditRequestStatus}
          />}
        </Grid>
      </Grid>
      {creditRequestType &&   <SendCreditRequestModal open={openSendRequestDialog}
        onClose={handleCloseRequestDialog}
        creditRequest={sendCreditRequest!}
        requestType={creditRequestType} 
        isStoreNeuk={isNeukFranchises}
      />}
      <MessagesSnackbar
        open={showResultMessage.open}
        onClose={closeProcessingAlert}
        message={showResultMessage.message}
        severity={showResultMessage.severity}
        duration={null}
      />
    </Box>

  );
};

export default CreditRequest;